import type { UserRole } from '@/types';
import type { TeamMember } from '@/types/admin';
import { ServiceError } from '../../contracts';
import type { PlatformUserService } from '../../platformContracts';
import { dataFor, delay, pctx, recordAudit, saveAdmin, uid } from './context';

const invalid = (m: string) => new ServiceError(m, 'INVALID');
const ASSIGNABLE_ROLES: UserRole[] = ['TENANT_OWNER', 'TENANT_ADMIN', 'STAFF'];

export const platformUsers: PlatformUserService = {
  async list(tenantId) {
    pctx('platform.tenants.manage');
    return delay(dataFor(tenantId).system.team);
  },
  async add(tenantId, input) {
    const { actor } = pctx('platform.tenants.manage');
    const t = dataFor(tenantId);
    if (!input.name.trim()) throw invalid('Name is required');
    if (!/^\S+@\S+\.\S+$/.test(input.email)) throw invalid('Enter a valid email');
    if (t.system.team.some((m) => m.email.toLowerCase() === input.email.trim().toLowerCase())) throw new ServiceError('This email is already on the team', 'CONFLICT');
    if (!ASSIGNABLE_ROLES.includes(input.role)) throw invalid('Choose a valid role');
    const member: TeamMember = { id: uid('tm'), name: input.name.trim(), email: input.email.trim().toLowerCase(), role: input.role, lastActiveAt: new Date().toISOString(), status: 'INVITED' };
    t.system.team.push(member);
    saveAdmin();
    recordAudit(actor, 'user.invited', 'USER', member.id, member.name, `Invited ${member.email} to ${t.name} as ${member.role.replace('_', ' ').toLowerCase()}`);
    return delay(member, 500);
  },
  async setRole(tenantId, memberId, role) {
    const { actor } = pctx('platform.tenants.manage');
    const t = dataFor(tenantId);
    const m = t.system.team.find((x) => x.id === memberId);
    if (!m) throw new ServiceError('Team member not found', 'NOT_FOUND');
    if (!ASSIGNABLE_ROLES.includes(role)) throw invalid('Choose a valid role');
    if (m.role !== role && m.role === 'TENANT_ADMIN' && t.system.team.filter((x) => x.role === 'TENANT_ADMIN' && x.status !== 'BLOCKED').length <= 1) {
      throw new ServiceError('Every store needs at least one active admin. Promote someone else first.', 'CONFLICT');
    }
    m.role = role;
    saveAdmin();
    recordAudit(actor, 'user.role_changed', 'USER', memberId, m.name, `Role changed to ${role.replace('_', ' ').toLowerCase()} on ${t.name}`);
    return delay(m, 400);
  },
  async setStatus(tenantId, memberId, status) {
    const { actor } = pctx('platform.tenants.manage');
    const t = dataFor(tenantId);
    const m = t.system.team.find((x) => x.id === memberId);
    if (!m) throw new ServiceError('Team member not found', 'NOT_FOUND');
    if (status === 'BLOCKED' && m.role === 'TENANT_ADMIN' && t.system.team.filter((x) => x.role === 'TENANT_ADMIN' && x.status !== 'BLOCKED').length <= 1) {
      throw new ServiceError('Every store needs at least one active admin. Promote someone else before blocking this one.', 'CONFLICT');
    }
    m.status = status;
    saveAdmin();
    recordAudit(actor, 'user.status_changed', 'USER', memberId, m.name, `${status === 'BLOCKED' ? 'Blocked' : status === 'ACTIVE' ? 'Reactivated' : 'Re-invited'} on ${t.name}`);
    return delay(m, 400);
  },
  async remove(tenantId, memberId) {
    const { actor } = pctx('platform.tenants.manage');
    const t = dataFor(tenantId);
    const m = t.system.team.find((x) => x.id === memberId);
    if (!m) throw new ServiceError('Team member not found', 'NOT_FOUND');
    if (m.role === 'TENANT_ADMIN' && t.system.team.filter((x) => x.role === 'TENANT_ADMIN').length <= 1) throw new ServiceError('Every store needs at least one admin. Add another admin before removing this one.', 'CONFLICT');
    t.system.team = t.system.team.filter((x) => x.id !== memberId);
    saveAdmin();
    recordAudit(actor, 'user.removed', 'USER', memberId, m.name, `Removed from ${t.name}`);
    return delay(undefined, 400);
  },
};
